import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            À propos
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            NYMPHEA
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            Tatoueuse passionnée, basée aux Sables-d'Olonne, je crée des œuvres uniques qui racontent votre histoire.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Image */}
            <div className="img-hover-zoom overflow-hidden">
              <img
                src="https://i.ibb.co/3yFV4nDT/IMG-6193.jpg"
                alt="NYMPHEA - Tatoueuse"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>

            {/* Text */}
            <div className="flex flex-col justify-center">
              <h2 className="text-2xl md:text-4xl font-heading font-light tracking-tight mb-8">
                Une passion devenue<br />
                <span className="italic">vocation</span>
              </h2>

              <div className="space-y-6 text-muted-foreground font-light leading-relaxed">
                <p>
                  Je tatoue depuis deux ans et me suis formée en autodidacte, guidée par ma passion pour l'art et le dessin. Ma pratique se veut <strong className="text-foreground">inclusive et safe pour tous·tes</strong>, car je crois que le tatouage doit être une expérience positive et bienveillante.
                </p>
                <p>
                  Mon approche se veut douce et à l'écoute. Chaque projet commence par un échange, une discussion pour comprendre vos envies, votre histoire, ce qui vous touche. Je crois profondément que le tatouage doit être une expérience positive, du premier contact jusqu'à la cicatrisation.
                </p>
                <p>
                  Spécialisée dans le <strong className="text-foreground">fine line</strong>, le <strong className="text-foreground">micro-réalisme</strong> et le <strong className="text-foreground">semi-réalisme</strong>, je m'inspire de la nature, des émotions humaines et de la nostalgie pour créer des pièces uniques et intemporelles.
                </p>
              </div>

              <div className="mt-12 pt-8 border-t border-border/40">
                <h3 className="text-lg font-heading mb-4">Autres univers que j'apprécie tatouer</h3>
                <p className="text-muted-foreground font-light leading-relaxed">
                  Au-delà de mes spécialités, j'aime explorer d'autres styles qui me passionnent : le <strong className="text-foreground">réalisme</strong>, le <strong className="text-foreground">cybersigilism</strong>, les inspirations <strong className="text-foreground">manga</strong> et <strong className="text-foreground">pop-culture</strong>, ainsi que les <strong className="text-foreground">motifs d'inspiration asiatique</strong>. N'hésitez pas à me soumettre vos idées, même les plus originales !
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Atelier Section */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                L'atelier
              </p>
              <h2 className="text-2xl md:text-4xl font-heading font-light tracking-tight mb-8">
                La Peauésie
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                C'est dans ce lieu lumineux et apaisant, mêlant tatouage et coffee shop, que je vous accueille pour donner vie à vos projets. L'Atelier La Peauésie est un espace pensé pour que vous vous sentiez bien, détendu·e, prêt·e à vivre cette expérience unique.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-8">
                Situé au cœur des Sables-d'Olonne, à quelques pas du port, l'atelier vous offre un cadre safe et chaleureux pour votre séance de tatouage.
              </p>
              <div className="flex items-start gap-3 text-sm mb-6">
                <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-medium">Atelier La Peauésie</p>
                  <p className="text-muted-foreground">
                    9 quai Ernest de Franqueville<br />
                    85100 Les Sables-d'Olonne
                  </p>
                </div>
              </div>
              
              {/* Social links for atelier */}
              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/atelier.la.peauesie/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="atelier-instagram"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  @atelier.la.peauesie
                </a>
                <a
                  href="https://www.tiktok.com/@latelier.la.peaus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="atelier-tiktok"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  @latelier.la.peaus
                </a>
              </div>
            </div>
            <div className="img-hover-zoom overflow-hidden">
              <img
                src="https://i.ibb.co/zHCxSRzF/IMG-8248.jpg"
                alt="Atelier La Peauésie"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-border/40">
        <div className="page-container text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-light tracking-tight mb-6">
            Envie de collaborer ?
          </h2>
          <p className="text-muted-foreground font-light max-w-lg mx-auto mb-10">
            Je serais ravie d'échanger sur votre projet et de créer ensemble quelque chose d'unique.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demande-projet" data-testid="about-cta-project">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs">
                Soumettre un projet
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-cta-contact">
              <Button variant="outline" className="h-12 px-8 uppercase tracking-widest text-xs border-foreground text-foreground hover:bg-foreground hover:text-background">
                Me contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
